// MUI
import { InputAdornment, InputAdornmentProps, TextField } from '@mui/material';
import { DesktopTimePicker } from '@mui/x-date-pickers-pro';

// components
import DatePicker from '../DatePicker/DatePicker';
import { GridCompact4, GridCompact8 } from '../Grid/Grid.components';

// styles
import {
  CompactGridContainer,
  StyledCustomTimePickerField,
  StyledInputAdornmentTypography
} from './PeriodInput.styles';

// types
import { PeriodInputProps } from './PeriodInput.types';

// react/utils
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

const PeriodInput = ({
  label,
  period,
  error,
  clearable = true,
  sameDayOnly,
  minDate,
  onChange
}: PeriodInputProps) => {
  const { t } = useTranslation('common');

  const { start, end } = period;

  const [startDate, setStartDate] = useState<Dayjs>();
  const [startTime, setStartTime] = useState<Dayjs>();
  const [durationHours, setDurationHours] = useState(String(0));
  const [durationMinutes, setDurationMinutes] = useState(String(0));

  // Make sure onChange updates only happen as a result
  // of manual changes, not the initial load
  const syncingFromProps = useRef(false);

  const totalMinutes = useMemo(
    () => Number(durationHours) * 60 + Number(durationMinutes),
    [durationHours, durationMinutes]
  );

  const normalizedStartDate = startDate?.second(0).millisecond(0);
  const normalizedStartTime = startTime?.second(0).millisecond(0);

  const fullStart = useMemo(() => {
    if (!normalizedStartDate || !normalizedStartTime) return;

    return normalizedStartDate
      .set('hour', normalizedStartTime.hour())
      .set('minute', normalizedStartTime.minute());
  }, [normalizedStartDate, normalizedStartTime]);

  const fullEnd = useMemo(() => {
    if (!fullStart) return;

    const calculatedEnd = fullStart.add(totalMinutes, 'minute');

    return sameDayOnly && !fullStart.isSame(calculatedEnd, 'day')
      ? fullStart.endOf('day')
      : calculatedEnd;
  }, [fullStart, totalMinutes, sameDayOnly]);

  const { minEndTime, maxEndTime } = useMemo(() => {
    if (!fullStart || !fullEnd)
      return {
        minEndTime: null as Dayjs | null,
        maxEndTime: null as Dayjs | null
      };

    if (sameDayOnly) {
      return {
        minEndTime: fullStart,
        maxEndTime: fullStart.endOf('day')
      };
    }

    const sameDay = fullStart.isSame(fullEnd, 'day');

    return {
      minEndTime: sameDay ? fullStart : null,
      maxEndTime: sameDay
        ? null
        : fullStart
            .set('hour', fullStart.hour())
            .set('minute', fullStart.minute())
    };
  }, [fullStart, fullEnd, sameDayOnly]);

  useEffect(() => {
    syncingFromProps.current = true;

    const dStart = dayjs(start).second(0).millisecond(0);
    const dEnd = dayjs(end).second(0).millisecond(0);

    setStartDate(dStart.startOf('day'));
    setStartTime(dStart);

    const diff = Math.max(dEnd.diff(dStart, 'minute'), 0);

    setDurationHours(String(Math.floor(diff / 60)));
    setDurationMinutes(String(diff % 60));
  }, [start, end]);

  useEffect(() => {
    if (syncingFromProps.current) {
      setTimeout(() => {
        syncingFromProps.current = false;
      });
    }
  }, [startDate, startTime, durationHours, durationMinutes]);

  useEffect(() => {
    if (!fullStart || !fullEnd || syncingFromProps.current) return;

    const propsStart = dayjs(start).second(0).millisecond(0);
    const propsEnd = dayjs(end).second(0).millisecond(0);

    const startChanged = !propsStart.isSame(fullStart, 'minute');
    const endChanged = !propsEnd.isSame(fullEnd, 'minute');

    if (!startChanged && !endChanged) return;

    onChange({
      start: fullStart.toDate(),
      end: fullEnd.toDate()
    });
  }, [fullStart, fullEnd]);

  const handleDateChange = (newDate: Date | null) => {
    if (!newDate) return;
    setStartDate(dayjs(newDate).startOf('day'));
  };

  const handleStartTimeChange = (newTime: Dayjs | null) => {
    if (!newTime) return;
    setStartTime(newTime);
  };

  const handleEndTimeChange = (newTime: Dayjs | null) => {
    if (!newTime || !fullStart) return;

    const diffMinutes = newTime.diff(fullStart, 'minute');
    const clamped = Math.max(diffMinutes, 0);

    setDurationHours(String(Math.floor(clamped / 60)));
    setDurationMinutes(String(clamped % 60));
  };

  const updateTimeValue = (
    ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    limit: number,
    setter: Dispatch<SetStateAction<string>>
  ) => {
    const value = ev.currentTarget.value;
    if (value.length > 2) return;

    let parsed = parseInt(value);
    if (isNaN(parsed)) parsed = 0;
    parsed = Math.max(0, Math.min(parsed, limit - 1));

    setter(String(parsed));
  };

  const CustomTimeOverflowInputAdornment = ({
    children,
    ...other
  }: InputAdornmentProps) => {
    const overflowDays =
      fullEnd?.startOf('day').diff(startTime?.startOf('day'), 'days') || 0;
    return (
      <>
        <InputAdornment {...other}>
          <StyledInputAdornmentTypography variant="body1">
            {overflowDays > 0 && `+${overflowDays}`}
          </StyledInputAdornmentTypography>
          {children}
        </InputAdornment>
      </>
    );
  };

  return (
    <CompactGridContainer>
      <GridCompact8>
        <DatePicker
          minDate={minDate}
          date={startDate?.toDate()}
          label={label || t('startDate')}
          onChange={handleDateChange}
          clearable={clearable}
          size="small"
        />
      </GridCompact8>
      <GridCompact4>
        <DesktopTimePicker
          ampm={false}
          value={startTime?.toDate() || null}
          onChange={(value, _ctx) => {
            const normalizedTime =
              value == null
                ? null
                : dayjs.isDayjs(value)
                ? value
                : dayjs(value);
            handleStartTimeChange(normalizedTime);
          }}
          label={t('from')}
          views={['hours', 'minutes']}
          enableAccessibleFieldDOMStructure={false}
          slots={{
            textField: StyledCustomTimePickerField
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              size: 'small'
            },
            openPickerButton: {
              tabIndex: -1
            }
          }}
        />
      </GridCompact4>
      <GridCompact4>
        <TextField
          value={durationHours}
          label={t('hours')}
          variant="outlined"
          fullWidth
          size="small"
          inputMode="numeric"
          slotProps={{
            htmlInput: {
              min: 0,
              max: 23,
              pattern: '[0-9]*',
              inputMode: 'numeric'
            }
          }}
          onChange={(event) => updateTimeValue(event, 24, setDurationHours)}
        />
      </GridCompact4>
      <GridCompact4>
        <TextField
          value={durationMinutes}
          label={t('minutes')}
          variant="outlined"
          fullWidth
          size="small"
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              pattern: '[0-9]*',
              min: 0,
              max: 59
            }
          }}
          onChange={(event) => updateTimeValue(event, 60, setDurationMinutes)}
        />
      </GridCompact4>
      <GridCompact4>
        <DesktopTimePicker
          ampm={false}
          value={fullEnd?.toDate() || null}
          minTime={minEndTime?.toDate() || undefined}
          maxTime={maxEndTime?.toDate() || undefined}
          label={t('until')}
          onChange={(value, _ctx) => {
            const normalizedTime =
              value == null
                ? null
                : dayjs.isDayjs(value)
                ? value
                : dayjs(value);
            handleEndTimeChange(normalizedTime);
          }}
          enableAccessibleFieldDOMStructure={false}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              size: 'small',
              error: !!error,
              helperText: error
            },
            openPickerButton: {
              tabIndex: -1
            }
          }}
          slots={{
            inputAdornment: CustomTimeOverflowInputAdornment,
            textField: StyledCustomTimePickerField
          }}
        />
      </GridCompact4>
    </CompactGridContainer>
  );
};

export default PeriodInput;
